import { useRouter } from 'next/router';
import { getQueryString } from '@framework/router';
import cfrUrl from '@/creditflowresearch/infrastructure/url';
import URL from '@/creditflowresearch/Router/urlConstants';
import { useProductsContext } from '@/creditflowresearch/application/Deal/contexts/ProductsContext';
import Product, { AbsProduct } from '@/creditflowresearch/domain/Product';
import Market from '@/creditflowresearch/domain/Market';
import Region from '@/creditflowresearch/domain/Region';
import { ProductAbbreviations } from '@/creditflowresearch/domain/Product/types/ProductType';
import UsMarket from '@/creditflowresearch/domain/Market/entities/UsMarket';
import EuropeanMarket from '@/creditflowresearch/domain/Market/entities/EuropeanMarket';
import { prioritizeProductAbbreviations } from '@/creditflowresearch/ui/common/utils/products/productStyleHelpers';

interface ILinkContext {
  markets: string[],
  isAllMarketsSelected: boolean,
  products: string[],
}

interface IQueryContext {
  products: string[],
  regions: string | string[],
  markets: string | string[],
  segments: string | string[],
}

interface ProductLinksConfig {
  label: string,
  getUrl: () => string,
  isAvailable?: (ctx: ILinkContext) => boolean,
  buildQuery: (ctx: IQueryContext) => Record<string, string | string[]>,
  getTooltip?: (ctx: ILinkContext) => string | null,
}

interface ILinkItem {
  name: string,
  label: string,
  url: string | null,
  tooltip: string | null,
}

const normalizeMarkets = (markets: string | string[] | undefined): string[] => {
  return Array.isArray(markets) ? markets : markets?.split(',') || [];
};

const getProductsQuery = (productIds: string[]): ProductAbbreviations[] => {
  return productIds
    .map((id) => Product.getAbbreviation(id))
    .filter((abbr): abbr is ProductAbbreviations => abbr !== null);
};

const getRegionsQuery = (regions: string | string[] | undefined, markets: string[]): string => {
  const marketRegions = markets
    .map((market: string) => Market.from(market)?.belongsToRegion()?.getAbbreviation())
    .filter(Boolean)
    .join(',');

  return [regions, marketRegions].filter(Boolean).join(',');
};

const getMarketsQuery = (markets: string | string[] | undefined, regions: string | string[] | undefined): string => {
  const regionIds = Region.getIds(regions || '');
  const regionMarkets = Region.getMarketsByRegions(regionIds).map((market) => market.getAbbreviation());

  return Array.from(new Set([...normalizeMarkets(markets), ...regionMarkets])).join(',');
};

const LINK_CONFIGS: Record<'abs' | 'corporate', Record<string, ProductLinksConfig>> = {
  abs: {
    bondScreener: {
      label: 'Bond Screener',
      getUrl: () => cfrUrl.getUrl(URL.ABS_BONDSCREENER),
      buildQuery: ({ products, regions }) => ({ products, regions }),
    },
    activeCalendar: {
      label: 'Active Calendar',
      getUrl: () => cfrUrl.getAbsActiveCalendarUrl(),
      buildQuery: ({ products, regions }) => ({ products, regions }),
    },
  },

  corporate: {
    bondScreener: {
      label: 'Bond Screener',
      getUrl: () => cfrUrl.getUrl(URL.CORPORATE_BONDSCREENER),
      buildQuery: ({ products, markets, segments }) => ({ products, markets, segments }),
    },
    activeCalendar: {
      label: 'Active Calendar',
      getUrl: () => cfrUrl.getCorporateActiveCalendarUrl(),
      isAvailable: (ctx) => Market.hasAnyOfType(ctx.markets, UsMarket.NAME),
      buildQuery: ({ products, segments }) => ({
        products,
        markets: UsMarket.ABBREVIATION,
        segments,
      }),
      getTooltip: (ctx) =>
        ctx.isAllMarketsSelected
          ? `US Market ${ ctx.products.join('/') } Active Calendar`
          : null,
    },
    pipeline: {
      label: 'Pipeline',
      getUrl: () => cfrUrl.getEuropeActiveCalendarUrl(),
      isAvailable: (ctx) => Market.hasAnyOfType(ctx.markets, EuropeanMarket.NAME),
      buildQuery: ({ products, segments }) => ({
        products,
        markets: EuropeanMarket.ABBREVIATION,
        segments,
      }),
      getTooltip: (ctx) =>
        ctx.isAllMarketsSelected
          ? `European Market ${ ctx.products.join('/') } Pipeline`
          : null,
    },
  },
};

const resolveLinks = (
  productType: 'abs' | 'corporate',
  linkContext: ILinkContext,
  queryContext: IQueryContext,
): ILinkItem[] => {
  const configs = LINK_CONFIGS[productType];

  return Object.entries(configs)
    .map(([name, config]) => {
      if (config.isAvailable && !config.isAvailable(linkContext)) {
        return null;
      }

      const query = config.buildQuery(queryContext);
      const queryString = getQueryString(query);

      return {
        name,
        label: config.label,
        url: `${ config.getUrl() }?${ queryString }`,
        tooltip: config.getTooltip?.(linkContext) || null,
      };
    })
    .filter((link): link is ILinkItem => link !== null);
};

export const useLinks = (): ILinkItem[] => {
  const { selectedTab } = useProductsContext();
  const { query: { regions, markets, segments } } = useRouter();

  if (!selectedTab) {
    return [];
  }

  const isAbs = Product.areAllOfType(selectedTab.productIds, AbsProduct.TYPE);
  const productType = isAbs ? 'abs' : 'corporate';
  const normalizedMarkets = normalizeMarkets(markets);

  const products = getProductsQuery(selectedTab.productIds);
  const isAllMarketsSelected = Market.containAllTypes(normalizedMarkets, [UsMarket.NAME, EuropeanMarket.NAME]);
  const productsAbbr = prioritizeProductAbbreviations(products);

  const linkContext: ILinkContext = {
    markets: normalizedMarkets,
    isAllMarketsSelected,
    products: productsAbbr,
  };

  const queryContext: IQueryContext = {
    products,
    regions: getRegionsQuery(regions, normalizedMarkets),
    markets: getMarketsQuery(markets, regions),
    segments,
  };

  return resolveLinks(productType, linkContext, queryContext);
};
